import { createLogger } from '@owl-lib/logger';
import { deserialize, fetchWrapper } from '../fetch';
import type {
  ClaimTemplate,
  ClaimTemplateListItem,
  TemplateSchema,
} from './interface';
const logger = createLogger(__filename);

export const CREATE_TEMPLATE_TIMEOUT = -1;
const templatesApiClient = {
  fetch: async (data: {
    dossier_id: string;
  }): Promise<{
    templates: ClaimTemplateListItem[];
    has_available_template: Record<string, boolean>;
    template_types: Record<string, TemplateSchema>;
  }> => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossier_id}/templates`,
      API_BASE_URL
    );
    logger.trace('templatesApiClient.fetch()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  fetchDetail: async (data: {
    dossier_id: string;
    template_id: string;
  }): Promise<ClaimTemplate> => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossier_id}/templates/${data.template_id}`,
      API_BASE_URL
    );
    logger.trace('templatesApiClient.fetchDetail()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  fetchTemplateState: async (data: {
    dossier_id: string;
    template_id: string;
  }) => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossier_id}/templates/${data.template_id}/state`,
      API_BASE_URL
    );
    logger.trace('templatesApiClient.fetchTemplateState()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  fetchTemplateSchemas: async (): Promise<string[]> => {
    const url = new URL(`/app/v1/templates/template_schemas`, API_BASE_URL);
    logger.trace('templatesApiClient.fetchTemplateSchemas()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  create: async (data: {
    dossier_id: string;
    template_type: string;
  }): Promise<void> => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossier_id}/templates`,
      API_BASE_URL
    );
    logger.trace('templatesApiClient.fetch()');
    return deserialize(
      await fetchWrapper(
        url.href,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            template_type: data.template_type,
          }),
        },
        CREATE_TEMPLATE_TIMEOUT
      )
    );
  },
  downloadHtmlTemplateAsRtf: async (data: {
    dossierId: string;
    templateId: string;
    template_version_id?: string;
  }): Promise<{ rtf_content: string }> => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossierId}/templates/${data.templateId}/download_html_as_rtf`,
      API_BASE_URL
    );
    if (data.template_version_id) {
      url.searchParams.set('template_version_id', data.template_version_id);
    }
    logger.trace('templatesApiClient.downloadHtmlTemplateAsRtf()');
    return deserialize(
      await fetchWrapper(url.href, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  },
  restoreTemplateVersion: async (data: {
    dossier_id: string;
    template_id: string;
    version_id: string;
  }): Promise<{
    template_version: {
      html: string;
      template_version_id: string;
      created_at: string;
    };
  }> => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossier_id}/templates/${data.template_id}/versions/${data.version_id}/restore`,
      API_BASE_URL
    );
    logger.trace('templatesApiClient.restoreTemplateVersion()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'POST', credentials: 'include' })
    );
  },
};

export default templatesApiClient;
